import {
  AuthorizationMethod,
  DaysOfWeek,
  CustomerGroupFilterType,
  CustomerGroupFilterTypeValues,
  Price,
  PriceType,
  TimeUnit,
  TimeUnitValues,
} from 'subdomains/cpo/types/charging-policy';
import * as Yup from 'yup';

const durationSchema = Yup.object({
  enabled: Yup.boolean().optional().label('Enabled'),
  unit: Yup.mixed<TimeUnit>()
    .oneOf(TimeUnitValues)
    .default(TimeUnit.HOUR)
    .label('Unit'),
  min: Yup.number().optional(),
  max: Yup.number().optional(),
});

const dateRangeSchema = Yup.object({
  enabled: Yup.boolean().optional().label('Enabled'),
  start: Yup.date().optional().label('Start'),
  end: Yup.date().optional().label('End'),
});

const timeOfDaySchema = Yup.object({
  enabled: Yup.boolean().optional().label('Enabled'),
  start: Yup.string().optional().label('Start'),
  end: Yup.string().optional().label('End'),
});

const daysOfWeekSchema = Yup.object({
  enabled: Yup.boolean().optional().label('Enabled'),
  values: Yup.array(Yup.string().oneOf(Object.values(DaysOfWeek)))
    .when('enabled', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    })
    .label('Days of week'),
});

const energySchema = Yup.object({
  enabled: Yup.boolean().optional().label('Enabled'),
  min: Yup.number().optional().label('Min'),
  max: Yup.number().optional().label('Max'),
});

const restrictionsSchema = Yup.object({
  enabled: Yup.boolean().optional(),
  timeOfDay: timeOfDaySchema.optional(),
  daysOfWeek: daysOfWeekSchema.optional(),
  date: dateRangeSchema.optional(),
  duration: durationSchema.optional(),
  energy: energySchema.optional(),
});

const basePriceSchema = Yup.object({
  type: Yup.mixed<PriceType>()
    .oneOf(Object.values(PriceType))
    .required()
    .label('Price type'),
  value: Yup.number().required().min(0).label('Price'),
  stepSize: Yup.number().min(0).max(3600).optional().label('Step size'),
});

const timePriceSchema = basePriceSchema.shape({
  type: Yup.mixed<PriceType>()
    .oneOf([PriceType.TIME, PriceType.PARKING_TIME])
    .required()
    .label('Price type'),
  unit: Yup.mixed<TimeUnit>()
    .oneOf(TimeUnitValues)
    .default(TimeUnit.HOUR)
    .label('Unit'),
});

const dynamicPriceSchema = Yup.object({
  type: Yup.mixed<PriceType>()
    .oneOf([PriceType.DYNAMIC])
    .required()
    .label('Price type'),
  surcharge: Yup.number().required().min(0).label('Surcharge'),
  backup: Yup.number().required().min(0).label('Backup'),
});

const priceSchema = Yup.lazy((value: Price) => {
  switch (value?.type) {
    case 'TIME':
    case 'PARKING_TIME':
      return timePriceSchema;
    case 'DYNAMIC':
      return dynamicPriceSchema;
    default:
      return basePriceSchema;
  }
});

export const tariffComponentSchema = Yup.object({
  price: priceSchema.optional(),
  restrictions: restrictionsSchema.optional(),
});

export const descriptionSchema = Yup.object({
  languageCode: Yup.string().required().label('Language'),
  text: Yup.string().required().max(512).label('Text'),
});

export const tariffSchema = Yup.object({
  components: Yup.array(tariffComponentSchema)
    .min(1)
    .required()
    .label('Tariff components'),
  descriptions: Yup.array(descriptionSchema).optional(),
});

export const tariffRuleSchema = Yup.object({
  authorizationMethod: Yup.array(Yup.mixed<AuthorizationMethod>()),
  customerGroups: Yup.array(Yup.string()).optional(),
  validityPeriod: Yup.object({
    enabled: Yup.boolean().optional(),
    start: Yup.date().optional(),
    end: Yup.date().optional(),
  }).test(
    'at-least-one-date',
    'Either start or end date must be provided when enabled',
    function (value) {
      if (!value?.enabled) return true;
      return !!(value.start || value.end);
    }
  ),
  tariff: tariffSchema.required(),
});

export const sessionThresholdsSchema = Yup.object({
  minTimeMinutes: Yup.number().optional(),
  maxTimeMinutes: Yup.number().optional(),
  minKwhConsumed: Yup.number().optional(),
  maxKwhConsumed: Yup.number().optional(),
  maxCost: Yup.number().optional(),
});

export const chargingPolicySchema = Yup.object({
  label: Yup.string().required().label('Name'),
  description: Yup.string().optional(),
  currency: Yup.string().required(),
  sessionThresholds: sessionThresholdsSchema.optional(),
  tariffRules: Yup.array(tariffRuleSchema)
    .min(1)
    .required()
    .label('Tariff rules'),
});

export const customerGroupSchema = Yup.object({
  label: Yup.string().required().label('Name'),
  description: Yup.string().optional(),
  filters: Yup.array()
    .of(
      Yup.object({
        type: Yup.mixed<CustomerGroupFilterType>()
          .oneOf(Object.values(CustomerGroupFilterTypeValues))
          .required(),
        values: Yup.array().of(Yup.string().required()).min(1).required(),
      })
    )
    .min(1)
    .required(),
});
